import React, { useState, useEffect, useImperativeHandle } from 'react';
import api from '../api';
import './TaskGrid.css';
import LoadingButton from '../UI/LoadingButton';
import Toast from '../UI/Toast';

const TaskGrid = React.forwardRef((props, ref) => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasPendingTasks, setHasPendingTasks] = useState(false);
    const [deletingTask, setDeletingTask] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const checkPendingTasks = (tasksList) => {
        return tasksList.some(task => task.status === 'TODO' || task.status === 'IN_PROGRESS');
    };

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const response = await api.get('/tasks');
            const newTasks = response.data.tasks || [];
            setTasks(newTasks);
            setHasPendingTasks(checkPendingTasks(newTasks));
            setError(null);
        } catch (error) {
            console.error('Error fetching tasks:', error);
            setError('Failed to load tasks');
            setTasks([]);
            setHasPendingTasks(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTasks();
    }, []);

    useEffect(() => {
        let intervalId;
        
        if (hasPendingTasks) {
            intervalId = setInterval(fetchTasks, 10000);
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [hasPendingTasks]);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
    };

    const handleDelete = async (guid) => {
        setDeletingTask(guid);
        try {
            await api.delete(`/tasks/${guid}`);
            setTasks(tasks.filter(task => task.guid !== guid));
            showToast('Task deleted successfully');
        } catch (error) {
            console.error('Error deleting task:', error);
            showToast('Failed to delete task', 'error');
        } finally {
            setDeletingTask(null);
        }
    };

    const handleDownload = async (guid, format) => {
        try {
            const response = await api.post(`/tasks/${guid}/export`, 
                { format },
                { responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `book.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            showToast(`File downloaded successfully`);
        } catch (error) {
            console.error('Error downloading file:', error);
            showToast('Failed to download file', 'error');
        }
    };

    const handleExport = async (guid, format) => {
        try {
            const response = await api.post(`/tasks/${guid}/export`, 
                { format },
                { responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `book.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting file:', error);
            setError('Failed to export file');
        }
    };

    const formatStatus = (status) => {
        return status
            .toLowerCase()
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    useImperativeHandle(ref, () => ({
        fetchTasks
    }));

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="task-grid">
            {loading ? (
                <div className="loading-message">Loading tasks...</div>
            ) : error ? (
                <div className="error-message">{error}</div>
            ) : tasks.length === 0 ? (
                <div className="empty-state">
                    <span className="material-symbols-outlined">book_4</span>
                    <p>No books generated yet</p>
                    <p className="subtitle">Your generated books will appear here</p>
                </div>
            ) : (
                tasks.map(task => (
                    <div key={task.guid} className="task-card">
                        <div className="task-header">
                            <h3>{task.title || 'Untitled'}</h3>
                            <span className={`task-status status-${task.status.toLowerCase()}`}>
                                {formatStatus(task.status)}
                            </span>
                        </div>
                        <div className="task-info">
                            <p>Author: {task.author}</p>
                            <p>Created: {new Date(task.created_date * 1000).toLocaleDateString()}</p>
                        </div>
                        <div className="task-actions">
                            <LoadingButton 
                                onClick={() => handleDelete(task.guid)}
                                isLoading={deletingTask === task.guid}
                                title="Delete"
                                className="delete-button"
                                disabled={task.status !== 'DONE'}
                            >
                                <i className="fa-solid fa-trash"></i>
                            </LoadingButton>
                            <div className="task-export-buttons">
                                <button 
                                    onClick={() => handleDownload(task.guid, 'pdf')} 
                                    title="Download PDF"
                                    disabled={task.status !== 'DONE'}
                                >
                                    <i className="fa-solid fa-file-pdf"></i>
                                </button>
                                <button 
                                    onClick={() => handleDownload(task.guid, 'docx')} 
                                    title="Download Word"
                                    disabled={task.status !== 'DONE'}
                                >
                                    <i className="fa-solid fa-file-word"></i>
                                </button>
                                <button 
                                    onClick={() => handleDownload(task.guid, 'txt')} 
                                    title="Download Text"
                                    disabled={task.status !== 'DONE'}
                                >
                                    <i className="fa-solid fa-file-lines"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                ))
            )}
            
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                    duration={toast.type === 'error' ? 10000 : 3000}
                />
            )}
        </div>
    );
});

export default TaskGrid; 